import React, { use } from "react";
import { createPlatformStackNavigator } from "./createPlatformStackNavigator";
import { Platform, StyleSheet } from "react-native";
import { useTheme } from "../hooks/use-theme";
import Icon from "../components/Icon";
import { useCardStyleInterpolator } from "../hooks/use-card-style-interpolator";
import ProfileScreen from "../screens/user/ProfileScreen";
import ScreenHeader from "../screens/user/ScreenHeader";
import InvoicesScreen from "../screens/client/InvoiceList";
import ColleaguesScreen from "../screens/client/ColleagueList";
import ColleagueDetailsScreen from "../screens/client/ColleagueDetails";
import ColleagueContractsScreen from "../screens/client/ColleagueContracts";
import { useI18n } from "../hooks/i18n-hooks";
import UserLogsScreen from "../screens/user/UserLogs";
import { ProfileSwitcherManagerContext } from "../contexts/ProfileSwitcherManager";
import { useCurrentProfile } from "../hooks/use-current-profile";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import MissionDetailsScreen from "../screens/user/MissionDetails";
import IJMConfigurationListScreen from "../screens/client/IJMConfigurationListScreen";
import { IjmSelection } from "../utils";
import IJMSelectionViewer from "../components/editors/ijm/screens/IJMSelectionViewer";

export type ProfileStackParamList = {
    Profile: undefined;
    UserLogs: undefined;
    Invoices: undefined;
    Colleagues: undefined;
    ColleagueDetails: { colleagueId: string };
    ColleagueContracts: { colleagueId: string };
    MissionDetails: { missionId: string };
    IJMConfigurationListScreen: undefined;
    IJMSelectionViewer: { cctLabel?: string; ijmSelection?: IjmSelection };
};

const Stack = createPlatformStackNavigator<ProfileStackParamList>();

const ProfileStack = () => {
    const cardStyleInterpolator = useCardStyleInterpolator();
    const i18n = useI18n();
    const theme = useTheme();
    const insets = useSafeAreaInsets();
    const { profileType } = use(ProfileSwitcherManagerContext);
    const currentProfile = useCurrentProfile();

    const isPrivateClient = currentProfile?.isPrivateClient || false;

    return (
        // On native the default-header screens show the system back button; the custom back icon +
        // testID and the card interpolator (dim backdrop, no-animation-on-web) are JS-stack-only and
        // kept on web. Native uses its default push animation.
        <Stack.Navigator
            screenOptions={{
                headerShown: false,
                headerBackButtonDisplayMode: "minimal",
                headerTitleAlign: "center",
                gestureEnabled: true,
                headerShadowVisible: false,
            }}
            webScreenOptions={{
                cardStyleInterpolator,
                headerStatusBarHeight: insets.top,
                // native-stack's headerTitleStyle is narrower (no lineHeight/letterSpacing) and only
                // accepts a string color, so the themed title font is applied on web only; native
                // default headers use the native title style.
                headerTitleStyle: {
                    ...theme.fonts.titleMedium,
                },
                headerBackImage: () => (
                    <Icon
                        name="back"
                        size={28}
                        style={{
                            marginLeft: Platform.OS === "ios" ? 10 : 0,
                            marginRight: 5,
                        }}
                    />
                ),
                headerBackTestID: "button-back",
            }}
            initialRouteName="Profile"
        >
            <Stack.Screen
                name="Profile"
                component={ProfileScreen}
                options={{
                    headerShown: true,
                    title: i18n.t("screens.client_profile.tabs.profile.title"),
                    header: () => (
                        <ScreenHeader
                            label={i18n.t(
                                `screens.profile_editor.title_${!isPrivateClient ? profileType : "private_client"}`,
                            )}
                        />
                    ),
                }}
            />
            <Stack.Screen
                name="UserLogs"
                component={UserLogsScreen}
                options={{
                    headerShown: true,
                    title: i18n.t("screens.user.activity_log.title"),
                }}
            />
            <Stack.Screen
                name="Invoices"
                component={InvoicesScreen}
                options={{
                    headerShown: true,
                    title: i18n.t("screens.user.my_invoices.title"),
                }}
            />
            <Stack.Screen
                name="Colleagues"
                component={ColleaguesScreen}
                options={{
                    headerShown: true,
                    title: i18n.t("screens.user.my_colleagues.title"),
                }}
            />
            <Stack.Screen
                name="IJMConfigurationListScreen"
                component={IJMConfigurationListScreen}
                options={{
                    headerShown: true,
                    title: i18n.t("ijm.configuration_list.screen_title"),
                }}
            />
            <Stack.Screen
                name="IJMSelectionViewer"
                component={IJMSelectionViewer}
                options={{
                    headerShown: true,
                    title: i18n.t("ijm.screen_title"),
                }}
            />
            <Stack.Screen
                name="ColleagueDetails"
                component={ColleagueDetailsScreen}
                options={{
                    headerShown: true,
                    title: i18n.t("screens.user.colleague_details.colleague"),
                }}
            />
            <Stack.Screen
                name="ColleagueContracts"
                component={ColleagueContractsScreen}
                options={{
                    headerShown: true,
                    title: i18n.t("screens.user.colleague_details.contracts"),
                }}
            />
            <Stack.Screen
                name="MissionDetails"
                component={MissionDetailsScreen}
                options={{
                    headerShown: false,
                    title: i18n.t("mission.nav_title"),
                    gestureEnabled: true,
                }}
            />
        </Stack.Navigator>
    );
};

export default ProfileStack;
